// src/components/Admin/DashboardOverview.js - FIXED VERSION
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  alpha,
  useTheme,
  Button,
} from '@mui/material';
import {
  People,
  LocalHospital,
  Business,
  Assignment,
  Assessment,
  TrendingUp,
  AccessTime,
  CheckCircle,
  PendingActions,
  Refresh,
  InsertDriveFile, // FIXED: Added missing import
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import StatsCard from './StatsCard';
import { getUsers } from '../../services/users';
import { getFacilities } from '../../services/facilities';
import { getStudies } from '../../services/studies';
import { getStudyStatistics } from '../../services/analytics';
import api from '../../services/api';

// Color palette for charts
const CHART_COLORS = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#00A3A3',
  purple: '#8B5CF6',
  pink: '#EC4899',
};

const MODALITY_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.success,
  CHART_COLORS.warning,
  CHART_COLORS.error,
  CHART_COLORS.info,
  CHART_COLORS.purple,
  CHART_COLORS.pink,
];

function DashboardOverview() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeFacilities: 0,
    pendingUsers: 0,
    totalStudies: 0,
    totalInstances: 0,
    completedReports: 0,
    activeRadiologists: 0,
    pendingReports: 0,
    avgTAT: 0,
    uniqueModalities: 0,
  });
  
  // Sync state
  const [syncGap, setSyncGap] = useState(0); // Orthanc studies not yet in MongoDB
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Chart data states
  const [studiesOverTime, setStudiesOverTime] = useState([]);
  const [modalityBreakdown, setModalityBreakdown] = useState([]);
  const [reportStatusData, setReportStatusData] = useState([]);
  const [weeklyAvgTAT, setWeeklyAvgTAT] = useState([]);

  const handleSyncOrthanc = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/admin/sync-orthanc');
      setSyncResult(res.data);
      setSyncGap(0);
      fetchDashboardData();
    } catch (err) {
      setSyncResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setSyncing(false);
    }
  };

  const sumData = (arr, key) => {
    if (!Array.isArray(arr)) return 0;
    return arr.reduce((s, item) => {
      const k = key || (item.value !== undefined ? 'value' : item.studies !== undefined ? 'studies' : 'avgTAT');
      return s + (Number(item[k] || 0));
    }, 0);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch admin statistics endpoint FIRST for accurate counts
      const adminStatsResponse = await api.get('/admin/statistics').catch(err => {
        console.error('Admin stats error:', err);
        return null;
      });

      // Store admin data for later use
      let adminStudyCount = null;
      let adminImageCount = null;
      let adminReportsCount = null;

      if (adminStatsResponse?.data?.success) {
        const adminData = adminStatsResponse.data;
        const mongo = adminData.mongodb;
        const orthanc = adminData.orthanc;
        
        console.log('Admin statistics:', {
          mongoStudies: mongo.totalStudies,
          orthancStudies: orthanc.CountStudies,
          syncProgress: adminData.sync.progress
        });
        
        // Show sync status if in progress
        if (adminData.sync.inProgress) {
          const syncMsg = `⏳ Syncing: ${adminData.sync.progress}% complete (${adminData.sync.mongoStudies}/${adminData.sync.orthancStudies} studies)`;
          console.log(syncMsg);
        }
        
        // Orthanc is the source of truth for DICOM studies and instances
        adminStudyCount = orthanc?.CountStudies ?? mongo.totalStudies ?? 0;
        adminImageCount = orthanc?.CountInstances ?? mongo.totalImages ?? 0;
        // Compute sync gap (studies in Orthanc not yet mirrored in MongoDB)
        const gap = Math.max(0, (orthanc?.CountStudies || 0) - (mongo.totalStudies || 0));
        setSyncGap(gap);
        // Global report totals (server-side accurate sum across facilities)
          adminReportsCount = mongo.reports?.completedReports ?? null;
      }

      const weeksBack = 12; // show weekly totals for the last 12 weeks
      const startISO = new Date(Date.now() - weeksBack * 7 * 24 * 60 * 60 * 1000).toISOString();

      const [
        usersResponse,
        facilitiesResponse,
        studyStatsResponse,
        studiesResponse,
        recentStudiesResponse, // Fetch studies within timeframe for weekly chart
        radiologistsResponse,
        reportsSummaryResponse,
      ] = await Promise.all([
        getUsers({ limit: 1000 }).catch(() => ({ users: [], pagination: { total: 0 } })),
        getFacilities({ limit: 1000 }).catch(() => ({ facilities: [], pagination: { total: 0 } })),
        getStudyStatistics().catch(() => null),
        getStudies({ limit: 1 }).catch(() => null),
        // Fetch recent studies covering the timeframe used for the chart
        getStudies({ limit: 5000, sort: '-createdAt', startDate: startISO }).catch(() => ({ studies: [] })),
        api.get('/admin/radiologists/performance').catch(() => ({ data: [] })),
        api.get('/admin/reports/summary').catch(() => ({ data: null })),
      ]);

      const users = usersResponse.users || [];
      const totalUsers = usersResponse.pagination?.total || users.length;

      // Debug: log responses to help trace zero-values
      console.debug('Dashboard responses:', {
        adminStatsResponse: adminStatsResponse?.data || null,
        studyStatsResponse: studyStatsResponse || null,
        studiesResponse: studiesResponse || null,
        recentStudiesCount: Array.isArray(recentStudiesResponse?.studies) ? recentStudiesResponse.studies.length : 0,
        usersCount: totalUsers,
      });

      // Process studies for accurate metrics and weekly aggregation
      const recentStudies = Array.isArray(recentStudiesResponse?.studies)
        ? recentStudiesResponse.studies
        : [];

      const studiesByWeek = {};
      const modalityCounts = {};
      const reportStatusCounts = { 
        completed: 0, 
        in_progress: 0, 
        pending: 0, 
        requested: 0 
      };
      const tatByDate = {}; // FIXED: Calculate real TAT by date

      // Prefer freshly-fetched reports summary (counts from Reports collection)
      const reportsSummary = reportsSummaryResponse?.data ?? null;
      if (reportsSummary && typeof reportsSummary.approvedReports === 'number') {
        adminReportsCount = reportsSummary.approvedReports;
      }

      recentStudies.forEach((study) => {
        // Group by studyDate (when scan was performed), falling back to createdAt
        const studyDateVal = study.studyDate || study.createdAt;
        if (studyDateVal) {
          const d = new Date(studyDateVal);
          // Compute week start (Monday) in UTC
          const dayIndex = (d.getUTCDay() + 6) % 7; // Monday=0, Sunday=6
          const weekStart = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dayIndex));
          const weekKey = weekStart.toISOString().split('T')[0];
          studiesByWeek[weekKey] = (studiesByWeek[weekKey] || 0) + 1;
        }

        // Count modality per study
        const modality = study.modality || 
          (Array.isArray(study.modalities) && study.modalities[0]) || 
          'Unknown';
        modalityCounts[modality] = (modalityCounts[modality] || 0) + 1;

        // Count report status per study
        const status = study.dycovueReportStatus || 'pending';
        if (status === 'completed') {
          reportStatusCounts.completed++;
          
          // FIXED: Calculate real TAT from completed reports
          if (study.dycovueReportCompletedAt && study.dycovueReportRequestedAt) {
            const completedDate = new Date(study.dycovueReportCompletedAt);
            const dateKey = completedDate.toISOString().split('T')[0];
            
            const requestedTime = new Date(study.dycovueReportRequestedAt).getTime();
            const completedTime = new Date(study.dycovueReportCompletedAt).getTime();
            const tatMinutes = Math.round((completedTime - requestedTime) / (1000 * 60));
            
            if (!tatByDate[dateKey]) {
              tatByDate[dateKey] = { total: 0, count: 0 };
            }
            tatByDate[dateKey].total += tatMinutes;
            tatByDate[dateKey].count += 1;
          }
        } else if (status === 'in_progress') {
          reportStatusCounts.in_progress++;
        } else if (status === 'requested') {
          reportStatusCounts.requested++;
        } else {
          reportStatusCounts.pending++;
        }
      });

      // Prefer admin stats for pending users (accurate DB count, not limited to 1000 fetched)
      const pendingUsers = adminStatsResponse?.data?.mongodb?.pendingUsers ?? users.filter((u) => u.approvalStatus === 'pending').length;
      const activeRadiologists = radiologistsResponse.data?.filter(
        (r) => r.isAvailable !== false
      ).length || 0;

      const facilitiesList = facilitiesResponse.facilities || [];
      const activeFacilities = facilitiesList.filter((f) => f.status === 'active').length;

        // Sum report counts across all facilities (uses facility.reportCount set by backend)
        const facilitiesReportsSum = facilitiesList.reduce((sum, f) => sum + (Number(f.reportCount || 0)), 0);

      // Calculate completed reports: prefer admin stats (accurate, excludes working copies),
      // then reports summary, then facility sum (also now excludes working copies), then client-side count
      const completedReports = adminReportsCount !== null
        ? adminReportsCount
        : (facilitiesReportsSum > 0
          ? facilitiesReportsSum
          : (studyStatsResponse?.dycovueReports?.completed ?? reportStatusCounts.completed));

      // Prefer server-provided weekly aggregation when available
      if (studyStatsResponse?.weeklyStudies && Array.isArray(studyStatsResponse.weeklyStudies) && studyStatsResponse.weeklyStudies.length > 0) {
        const serverWeeks = studyStatsResponse.weeklyStudies.map(w => ({ date: w.label, studies: w.count }));
        setStudiesOverTime(serverWeeks);
      } else {
        // Prepare weekly studies over time for the last `weeksBack` weeks (client fallback)
        const weeksData = [];
        const now = new Date();
        const nowDayIndex = (now.getUTCDay() + 6) % 7;
        const currentWeekStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - nowDayIndex));
        for (let i = weeksBack - 1; i >= 0; i--) {
          const wk = new Date(currentWeekStart);
          wk.setUTCDate(currentWeekStart.getUTCDate() - i * 7);
          const key = wk.toISOString().split('T')[0];
          weeksData.push({
            date: wk.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            studies: studiesByWeek[key] || 0,
          });
        }
        setStudiesOverTime(weeksData);
      }

      // Prepare modality breakdown - prefer server-side aggregate if present
      let modalityData = [];
      if (Array.isArray(studyStatsResponse?.modalityBreakdown) && studyStatsResponse.modalityBreakdown.length > 0) {
        modalityData = studyStatsResponse.modalityBreakdown
          .map((m) => ({ name: m._id || m.name, value: m.count || m.value || 0 }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8);
      } else {
        modalityData = Object.entries(modalityCounts)
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8);
      }
      setModalityBreakdown(modalityData);

      // Prepare report status data - prefer server-side counts when available
      const serverReportCounts = studyStatsResponse?.reportStatusCounts;
      const finalReportCounts = serverReportCounts || reportStatusCounts;
      setReportStatusData([
        { name: 'Completed', value: finalReportCounts.completed || 0, color: CHART_COLORS.success },
        { name: 'In Progress', value: finalReportCounts.in_progress || 0, color: CHART_COLORS.warning },
        { name: 'Requested', value: finalReportCounts.requested || 0, color: CHART_COLORS.info },
        { name: 'Pending', value: finalReportCounts.pending || 0, color: CHART_COLORS.error },
      ]);

      // Set weekly average TAT series from server when available
      if (studyStatsResponse?.weeklyAvgTAT && Array.isArray(studyStatsResponse.weeklyAvgTAT)) {
        setWeeklyAvgTAT(studyStatsResponse.weeklyAvgTAT.map(w => ({ date: w.label, avgTAT: w.avgTAT })));
      } else if (studyStatsResponse?.weeklyStudies && Array.isArray(studyStatsResponse.weeklyStudies)) {
        setWeeklyAvgTAT(studyStatsResponse.weeklyStudies.map(w => ({ date: w.label, avgTAT: w.avgTAT || 0 })));
      } else {
        setWeeklyAvgTAT([]);
      }

      // Calculate average TAT from radiologists
      const radiologists = radiologistsResponse.data || [];
      const avgTAT =
        radiologists.length > 0
          ? radiologists.reduce((sum, r) => sum + (parseFloat(r.averageTat) || 0), 0) /
            radiologists.length
          : 0;

      // Calculate number of unique modalities (prefer server-side breakdown)
      const uniqueModalities = Array.isArray(studyStatsResponse?.modalityBreakdown)
        ? studyStatsResponse.modalityBreakdown.length
        : Object.keys(modalityCounts).length;

      // Use server stats when available
      const serverStudiesTotalFromList = studiesResponse?.totalStudies || studiesResponse?.pagination?.total || null;
      const serverTotalStudies = Math.max(
        studyStatsResponse?.totalStudies || 0,
        serverStudiesTotalFromList || 0
      );
      const serverTotalInstances = studyStatsResponse?.totalInstances || 0;

      // Prefer server-side dycovue report counts when present
      const pendingReportsFinal = studyStatsResponse?.dycovueReports?.pending ?? (reportStatusCounts.requested + reportStatusCounts.pending);

      setStats({
        totalUsers,
        activeFacilities,
        pendingUsers,
        // Use admin endpoint data if available (most accurate for counts)
        totalStudies: (typeof adminStudyCount === 'number' && adminStudyCount !== null) ? adminStudyCount : serverTotalStudies,
        totalInstances: (typeof adminImageCount === 'number' && adminImageCount !== null) ? adminImageCount : serverTotalInstances,
        completedReports,
        activeRadiologists,
        pendingReports: pendingReportsFinal,
        avgTAT: avgTAT.toFixed(1),
        uniqueModalities,
      });
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Listen for ORTHANC sync events
  useEffect(() => {
    const handler = (e) => {
      console.log('ORTHANC_SYNC_COMPLETE received in Admin DashboardOverview, refreshing dashboard');
      fetchDashboardData();
    };
    window.addEventListener('ORTHANC_SYNC_COMPLETE', handler);
    return () => window.removeEventListener('ORTHANC_SYNC_COMPLETE', handler);
  }, [fetchDashboardData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 1.5,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[8],
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="caption"
              sx={{ color: entry.color, display: 'block' }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dashboard Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's what's happening with your system today.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDashboardData}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          {syncGap > 0 && (
            <Button
              variant="contained"
              color="warning"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={handleSyncOrthanc}
              disabled={syncing}
              sx={{ textTransform: 'none' }}
            >
              {syncing ? 'Syncing…' : `Sync ${syncGap} Studies from Orthanc`}
            </Button>
          )}
        </Box>
      </Box>

      {/* Sync gap alert */}
      {syncGap > 0 && !syncing && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
          {syncGap} {syncGap === 1 ? 'study exists' : 'studies exist'} in Orthanc but have not been imported into MongoDB yet. Click "Sync from Orthanc" to mirror them.
        </Alert>
      )}
      {syncResult && (
        <Alert severity={syncResult.success ? 'success' : 'error'} sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSyncResult(null)}>
          {syncResult.success
            ? `Sync complete: ${syncResult.imported} imported, ${syncResult.alreadySynced} already synced, ${syncResult.errors} errors.`
            : `Sync failed: ${syncResult.error}`}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Core Metrics Section */}
      <Box mb={4}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1, color: 'text.secondary', fontFamily: '"Satoshi", sans-serif' }}>
          Core Metrics
        </Typography>
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              title="Total Studies"
              value={stats.totalStudies}
              subtitle={syncGap > 0 ? `${syncGap} not yet in DB` : 'Orthanc (source of truth)'}
              trendDirection="up"
              loading={loading}
              icon={<LocalHospital sx={{ color: syncGap > 0 ? '#F59E0B' : '#00A3A3', fontSize: 24 }} />}
            />
          </Grid>

          

          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              title="Total Users"
              value={stats.totalUsers}
              subtitle={`${stats.pendingUsers} pending approval`}
              trendDirection="up"
              loading={loading}
              onClick={() => navigate('/admin/users')}
              icon={<People sx={{ color: '#0067BC', fontSize: 24 }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              title="Active Facilities"
              value={stats.activeFacilities}
              subtitle="Currently operational"
              loading={loading}
              onClick={() => navigate('/admin/facilities')}
              icon={<Business sx={{ color: '#00A3A3', fontSize: 24 }} />}
            />
          </Grid>
 
          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              title="Total Images"
              value={Number(stats.totalInstances || 0).toLocaleString()}
              subtitle="DICOM instances"
              loading={loading}
              icon={<InsertDriveFile sx={{ color: '#EC4899', fontSize: 24 }} />}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Report Metrics Section */}
      <Box mb={2}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1, color: 'text.secondary', fontFamily: '"Satoshi", sans-serif' }}>
          Report Metrics
        </Typography>
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              title="Completed Reports"
              value={stats.completedReports}
              subtitle={`${stats.pendingReports} pending`}
              trendDirection="up"
              loading={loading}
              icon={<CheckCircle sx={{ color: '#22C55E', fontSize: 24 }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              title="Pending Reports"
              value={stats.pendingReports}
              subtitle="Awaiting radiologist"
              loading={loading}
              icon={<PendingActions sx={{ color: '#F59E0B', fontSize: 24 }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <StatsCard
              title="Avg. Turnaround Time"
              value={`${stats.avgTAT}min`}
              subtitle="Report completion"
              loading={loading}
              icon={<AccessTime sx={{ color: '#34C3FF', fontSize: 24 }} />}
            />
          </Grid>

          
        </Grid>
      </Box>

      {/* System Metrics Section */}
      <Box mb={2}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1, color: 'text.secondary', fontFamily: '"Satoshi", sans-serif' }}>
          System Metrics
        </Typography>
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={12} sm={6} lg={4}>
            <StatsCard
              title="Active Radiologists"
              value={stats.activeRadiologists}
              subtitle="Available for assignments"
              loading={loading}
              onClick={() => navigate('/admin/radiologists')}
              icon={<Assignment sx={{ color: '#8B5CF6', fontSize: 24 }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <StatsCard
              title="Modality Types"
              value={stats.uniqueModalities}
              subtitle="Different modalities"
              loading={loading}
              icon={<Assessment sx={{ color: '#3E6AE1', fontSize: 24 }} />}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Charts Section */}
      <Grid container spacing={1.5} mb={2}>
        {/* Studies Over Time */}
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Studies Over Time
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 12 weeks (by study date)
                  </Typography>
                </Box>
                <TrendingUp sx={{ color: CHART_COLORS.primary, fontSize: 32 }} />
              </Box>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              ) : sumData(studiesOverTime, 'studies') === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={studiesOverTime}>
                    <defs>
                      <linearGradient id="colorStudies" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="date"
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis stroke={theme.palette.text.secondary} style={{ fontSize: '12px' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="studies"
                      stroke={CHART_COLORS.primary}
                      fillOpacity={1}
                      fill="url(#colorStudies)"
                      name="Studies"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Modality Breakdown */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Modality Breakdown
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Study distribution
                  </Typography>
                </Box>
                <Assessment sx={{ color: CHART_COLORS.secondary, fontSize: 32 }} />
              </Box>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              ) : sumData(modalityBreakdown, 'value') === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={modalityBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {modalityBreakdown.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={MODALITY_COLORS[index % MODALITY_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Second Row of Charts */}
      <Grid container spacing={1.5} mb={2}>
        {/* Report Status */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Report Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current distribution
                  </Typography>
                </Box>
                <Assignment sx={{ color: CHART_COLORS.success, fontSize: 32 }} />
              </Box>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              ) : sumData(reportStatusData, 'value') === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportStatusData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis type="number" stroke={theme.palette.text.secondary} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      stroke={theme.palette.text.secondary}
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                      {reportStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Weekly Avg TAT Summary */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Weekly Avg. TAT
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average minutes per completed report (last 12 weeks)
                  </Typography>
                </Box>
                <AccessTime sx={{ color: CHART_COLORS.warning, fontSize: 32 }} />
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              ) : (!Array.isArray(weeklyAvgTAT) || weeklyAvgTAT.length === 0) ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <Typography color="text.secondary">No TAT data available yet</Typography>
                </Box>
              ) : (
                <Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography variant="h3" fontWeight={700} sx={{ mr: 2 }}>
                      {Number(weeklyAvgTAT[weeklyAvgTAT.length - 1].avgTAT || 0).toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">min (most recent week)</Typography>
                  </Box>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={weeklyAvgTAT} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis dataKey="date" stroke={theme.palette.text.secondary} style={{ fontSize: '12px' }} />
                      <YAxis stroke={theme.palette.text.secondary} style={{ fontSize: '12px' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="avgTAT" stroke={CHART_COLORS.warning} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
}

export default DashboardOverview;